import React, { useRef, useState } from "react";
import { Camera, KeyRound, Loader2, Monitor, Moon, Save, Smartphone, Sun, User2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdatetime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useInstallPrompt } from "@/lib/pwa";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/pages/crm/LeadForm";
import { cn } from "@/lib/utils";

const ROLE_LABEL = { admin: "Administrator", sales: "Sales Staff", telecaller: "Telecaller", accountant: "Accountant", staff: "Other Staff" };
const THEMES = [
  { key: "light", label: "Light", icon: Sun },
  { key: "dark", label: "Dark", icon: Moon },
  { key: "system", label: "System", icon: Monitor },
];

const InstallCard = () => {
  const { canInstall, install, installed, ios } = useInstallPrompt();
  if (installed) {
    return (
      <p className="mt-3 rounded-lg border border-success/40 bg-success-soft px-3 py-2 text-xs font-medium text-success"
        data-testid="install-card">
        <Smartphone className="mr-1.5 inline h-3.5 w-3.5" /> Running as an installed app on this device.
      </p>
    );
  }
  if (!canInstall && !ios) return null;
  return (
    <div className="mt-3 rounded-lg border border-primary/30 bg-primary-soft/50 px-3 py-2.5" data-testid="install-card">
      <p className="text-xs font-semibold">Use this on your phone</p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        {canInstall
          ? "Install it on this device and it opens full screen with its own icon."
          : "In Safari, tap Share and then \u201cAdd to Home Screen\u201d."}
      </p>
      {canInstall && (
        <Button size="sm" variant="soft" className="mt-2 w-full" data-testid="profile-install-btn" onClick={install}>
          <Smartphone className="h-3.5 w-3.5" /> Install app
        </Button>
      )}
    </div>
  );
};

export default function Profile() {
  const { user, setUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const fileRef = useRef();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", avatar: user?.avatar || "" });
  const [pw, setPw] = useState({ current: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) return toast.error("Please choose an image smaller than 1.5 MB.");
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, avatar: reader.result }));
    reader.readAsDataURL(f);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch("/auth/me", { ...form, theme });
      setUser(data.user);
      toast.success("Profile updated");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) return toast.error("The new passwords do not match.");
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current: pw.current, new_password: pw.new_password });
      toast.success("Password changed");
      setPw({ current: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(errMsg(err)); } finally { setPwBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader icon={User2} title="My Profile" subtitle="Your personal details, appearance and password. Company settings live in Admin Settings." />

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Identity card */}
        <Card className="grain overflow-hidden lg:col-span-1">
          <div className="bg-gradient-panel px-6 pb-6 pt-8 text-center">
            <div className="relative inline-block">
              <UserAvatar key={user?.email} name={form.name} src={form.avatar} size="xl" ring />
              <button
                onClick={() => fileRef.current?.click()}
                className="absolute -bottom-1 -right-1 grid h-8 w-8 place-items-center rounded-full bg-primary text-primary-foreground shadow-md transition-transform hover:scale-110"
                data-testid="avatar-upload"
              >
                <Camera className="h-4 w-4" />
              </button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickImage} />
            </div>
            <h2 className="mt-4 font-display text-xl font-bold">{user?.name}</h2>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <StatusBadge status={ROLE_LABEL[user?.role] || user?.role} tone="primary" />
              <StatusBadge status={user?.active ? "Active" : "Inactive"} />
            </div>
          </div>
          <CardContent className="space-y-2 pt-5 text-sm">
            <Row label="Designation" value={user?.designation || "\u2014"} />
            <Row label="Department" value={user?.department || "\u2014"} />
            <Row label="Account created" value={fdatetime(user?.created_at)} />
            <Row label="Last login" value={fdatetime(user?.last_login)} />
            <InstallCard />
          </CardContent>
        </Card>

        <div className="space-y-5 lg:col-span-2">
          {/* Details */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-display text-base">Personal details</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={save} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Full name" required>
                    <Input required value={form.name} data-testid="profile-name"
                      onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </Field>
                  <Field label="Phone number">
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </Field>
                  <Field label="Email address" hint="Only an administrator can change your email.">
                    <Input value={user?.email} disabled />
                  </Field>
                  <Field label="Role" hint="Assigned by your administrator.">
                    <Input value={ROLE_LABEL[user?.role] || user?.role} disabled />
                  </Field>
                </div>

                <Field label="Appearance">
                  <div className="grid grid-cols-3 gap-2">
                    {THEMES.map((t) => (
                      <button key={t.key} type="button" onClick={() => setTheme(t.key)} data-testid={`theme-${t.key}`}
                        className={cn(
                          "flex flex-col items-center gap-1.5 rounded-lg border p-3 text-xs font-medium transition-colors",
                          theme === t.key ? "border-primary bg-primary-soft text-primary" : "border-border hover:border-primary/40"
                        )}>
                        <t.icon className="h-4 w-4" /> {t.label}
                      </button>
                    ))}
                  </div>
                </Field>

                <Button type="submit" disabled={busy} data-testid="profile-save">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Password */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-display text-base">Change password</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={changePassword} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Current password" required>
                    <Input type="password" required value={pw.current} data-testid="pw-current"
                      onChange={(e) => setPw({ ...pw, current: e.target.value })} />
                  </Field>
                  <Field label="New password" required>
                    <Input type="password" required minLength={8} value={pw.new_password}
                      onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
                  </Field>
                  <Field label="Confirm new password" required>
                    <Input type="password" required minLength={8} value={pw.confirm}
                      onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
                  </Field>
                </div>
                <Button type="submit" variant="outline" disabled={pwBusy}>
                  {pwBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Update password
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 border-b border-border/60 py-2 last:border-0">
    <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className="text-right text-sm font-medium">{value}</span>
  </div>
);
